import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";
import { LockKey, Eye, EyeSlash, CheckCircle, WarningCircle, ArrowLeft } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "One number", test: (p) => /[0-9]/.test(p) },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Auto-redirect to login after success
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate("/login?reset=success"), 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const passwordValid = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordValid) { setError("Password does not meet the requirements."); return; }
    if (!passwordsMatch) { setError("Passwords do not match."); return; }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to reset password. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="reset-password-no-token">
        <div className="w-full max-w-sm space-y-6 text-center">
          <BrandLogo iconClassName="w-10 h-10 rounded-lg mx-auto" textClassName="text-2xl font-extrabold tracking-tight font-heading text-slate-900" />
          <div className="flex flex-col items-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <WarningCircle weight="duotone" className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-black font-heading text-slate-900">Invalid reset link</h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              This reset link is missing or malformed. Please request a new one.
            </p>
          </div>
          <Link
            to="/forgot-password"
            className="flex items-center justify-center gap-2 w-full h-11 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <BrandLogo
            iconClassName="w-10 h-10 rounded-lg"
            textClassName="text-2xl font-extrabold tracking-tight font-heading text-slate-900"
          />

          {success ? (
            /* Success state */
            <div className="space-y-6 text-center" data-testid="reset-password-success">
              <div className="flex flex-col items-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle weight="duotone" className="w-9 h-9 text-emerald-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-black tracking-tight font-heading text-slate-900">
                    Password updated!
                  </h1>
                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                    Your password has been reset. Redirecting you to sign in…
                  </p>
                </div>
              </div>
              <Link
                to="/login?reset=success"
                className="flex items-center justify-center gap-2 w-full h-11 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors"
              >
                Sign in now
              </Link>
            </div>
          ) : (
            /* Reset form */
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">
                  Set new password
                </h1>
                <p className="text-sm text-slate-500 mt-1 font-medium">
                  Choose a strong password for your account.
                </p>
              </div>

              {error && (
                <div data-testid="reset-password-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md font-medium flex items-start gap-2">
                  <WarningCircle weight="bold" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* New password */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                    New Password
                  </label>
                  <div className="relative">
                    <LockKey weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      data-testid="reset-password-new"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      className="w-full h-12 pl-10 pr-12 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black font-medium"
                      placeholder="New password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeSlash weight="bold" className="w-4 h-4" /> : <Eye weight="bold" className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Strength rules */}
                  {password.length > 0 && (
                    <ul className="mt-2.5 space-y-1">
                      {PASSWORD_RULES.map((rule) => {
                        const passed = rule.test(password);
                        return (
                          <li key={rule.label} className={`flex items-center gap-1.5 text-xs font-medium ${passed ? "text-emerald-600" : "text-slate-400"}`}>
                            <CheckCircle weight={passed ? "fill" : "regular"} className="w-3.5 h-3.5 flex-shrink-0" />
                            {rule.label}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <LockKey weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      data-testid="reset-password-confirm"
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      className={`w-full h-12 pl-10 pr-12 text-sm border rounded-md focus:outline-none focus:ring-2 font-medium transition-colors ${
                        confirmPassword.length > 0
                          ? passwordsMatch
                            ? "border-emerald-400 focus:ring-emerald-200"
                            : "border-red-300 focus:ring-red-200"
                          : "border-slate-300 focus:ring-black/10 focus:border-black"
                      }`}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirm ? <EyeSlash weight="bold" className="w-4 h-4" /> : <Eye weight="bold" className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="mt-1.5 text-xs font-medium text-red-500">Passwords do not match</p>
                  )}
                </div>

                <button
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={loading || !passwordValid || !passwordsMatch}
                  className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="flex items-center justify-center gap-1.5 text-sm text-slate-400 font-medium hover:text-slate-600 transition-colors"
                >
                  <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
                  Request a new reset link
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="relative z-10 text-center space-y-6 max-w-md">
          <LockKey weight="duotone" className="w-24 h-24 text-foreground mx-auto" />
          <h2 className="text-3xl font-black tracking-tight font-heading text-white">
            Keep your account secure
          </h2>
          <p className="text-slate-400 text-base font-medium leading-relaxed">
            Use a strong, unique password with uppercase letters, lowercase letters, and numbers.
          </p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
      </div>
    </div>
  );
}
